<template>
  <div class="simple-line-chart" :class="{ 'is-hovered': isHovered }">
    <svg :width="width + margin.left + margin.right" :height="height + margin.top + margin.bottom">
      <g :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }">
        <path ref="line" class="line" :d="pathDesc" />
        <circle ref="circle" class="circle" r="2" :cx="circlePos[0]" :cy="circlePos[1]" />
        <rect ref="overlay" class="overlay" :width="width" :height="height" />
      </g>
    </svg>
    <div class="tooltip" role="tooltip" :style="{ 'transform': `translate(${tooltipPos[0]}px, ${tooltipPos[1]}px)` }">
      <div class="tooltip-inner">
        <div class="title is-spaced">{{ tooltipValue }} {{ tooltipTitle }}</div>
        <div class="subtitle">{{ tooltipSubtitle }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { scaleTime, scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
import { select, mouse } from 'd3-selection';
import { extent, bisector } from 'd3-array';
import { format } from 'date-fns';
import { resizable } from 'views/mixins'

export default {

  mixins: [resizable],

  props: {
    data: {
      type: Array,
      required: true
    },
    tooltipTitle: {
      type: String,
      required: false
    },
    isDisabled: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      margin: { top: 3, right: 3, bottom: 3, left: 3 },
      width: 0,
      height: 0,
      x: null,
      y: null,
      valueLine: null,
      pathDesc: null,
      circlePos: [0, 0],
      isHovered: false,
      tooltipPos: [0, 0],
      tooltipValue: '',
      tooltipSubtitle: ''
    };
  },

  mounted() {
    this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
    this.height = this.$el.getBoundingClientRect().height - this.margin.top - this.margin.bottom;

    this.x = scaleTime().range([0, this.width]);
    this.y = scaleLinear().range([this.height, 0]);

    this.valueLine = line()
      .x(d => this.x(d.time))
      .y(d => this.y(d.value));
    
    this.x.domain(extent(this.data, d => d.time));
    this.y.domain(extent(this.data, d => d.value));

    this.pathDesc = this.valueLine(this.data);

    select(this.$refs.circle)
      .attr('cx', this.x(this.x.domain()[1]))
      .attr('cy', this.y(this.data[this.data.length - 1].value));

    select(this.$refs.overlay)
      .on('mouseover', () => {
        if (this.isDisabled) {
          return;
        }

        this.isHovered = true;
      })
      .on('mouseout', () => {
        if (this.isDisabled) {
          return;
        }

        this.isHovered = false;
        this.circlePos = [this.x(this.x.domain()[1]), this.y(this.data[this.data.length - 1].value)];
      })
      .on('mousemove', this.mousemove);
  },

  methods: {
    onResize() {
      this.width = 0;

      this.$nextTick(() => {
        this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
        this.x.range([0, this.width]);

        this.pathDesc = this.valueLine(this.data);

        select(this.$refs.circle)
          .attr('cx', this.x(this.x.domain()[1]));
      });
    },

    mousemove() {
      if (this.isDisabled) {
        return;
      }

      const bisectDate = bisector(d => d.time).left;
      const x0 = this.x.invert(mouse(this.$refs.overlay)[0]);
      const i = bisectDate(this.data, x0, 1);
      const d0 = this.data[i - 1];
      const d1 = this.data[i];
      let d;
      
      if (d1) {
        d = x0 - d0.time > d1.time - x0 ? d1 : d0;
      } else {
        d = d0;
      }

      this.circlePos = [this.x(d.time), this.y(d.value)];
      this.tooltipPos = [this.x(d.time), this.y(d.value)];
      this.tooltipValue = d.value;
      this.tooltipSubtitle = format(d.time, 'D MMM YYYY');
    }
  },

  watch: {
    data: function (newData) {
      this.data = newData;

      this.x.domain(extent(this.data, d => d.time));
      this.y.domain(extent(this.data, d => d.value));
      this.pathDesc = this.valueLine(this.data);
      this.circlePos = [this.x(this.x.domain()[1]), this.y(this.data[this.data.length - 1].value)];
    }
  }
};
</script>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.simple-line-chart {
  &.is-warning {
    .line {
      stroke: $warning;
    }

    .circle {
      fill: $warning;
    }
  }

  &.is-light {
    .line {
      stroke: #afafaf;
    }

    .circle {
      fill: #afafaf;
    }
  }

  &.is-hovered {
    .line {
      stroke: #afafaf;
    }

    .tooltip {
      opacity: 1;
    }
  }

  .line {
    fill: none;
    stroke: $black;
    stroke-width: 1.5px;
  }

  .circle {
    fill: $black;
  }

  .overlay {
    fill: none;
    pointer-events: all;
  }

  .tooltip {
    margin-left: 1rem;
    transition: none;

    .tooltip-inner {
      padding: .75rem;
    }

    .title {
      font-size: $size-small;
      color: $grey-darker;
      margin-bottom: .25rem;
    }

    .subtitle {
      font-size: .6875rem;
      color: $grey-darker;
      margin-bottom: 0;
    }
  }
}
</style>
